// The MSP's customers (#417): the workspaces it has asked to serve, each with
// what the customer has answered and how far its setup has come.
//
// **A customer invitation is not a team invitation.** This screen creates a
// customer's organisation and workspace, invites its admin, and asks for the
// capabilities the MSP wants; the admin answers each on their own workspace's
// Access grants page (#418), and until then the MSP holds nothing there. A
// colleague joining *this* organisation is the team panel on Settings.

import { useRef, useState, type FormEvent } from "react";

import {
  CAPABILITIES,
  CAPABILITY_TEXT,
  creationKey,
  type Capability,
  type Created,
  type CustomerRow,
  type GrantState,
} from "../api/customers";
import { addressRefusal, messageOf } from "../api/http";
import type { ContextChoice, SessionResponse } from "../api/session";
import { Chip, Empty, Field, Notice, PageIntro } from "./chrome";
import { useReadOnly } from "./readOnly";

export function CustomersPage({
  session,
  customers,
  error,
  onInvite,
  onEnter,
  onRequestAgain,
}: {
  session: SessionResponse;
  customers: CustomerRow[] | null;
  error: string | null;
  onInvite: (body: {
    name: string;
    admin_email: string;
    capabilities: Capability[];
    creation_key: string;
  }) => Promise<Created>;
  onEnter: (choice: ContextChoice) => Promise<void>;
  /** Ask again for the capabilities the customer declined or took back (#418). */
  onRequestAgain: (workspaceId: string, capabilities: Capability[]) => Promise<void>;
}) {
  const readOnly = useReadOnly();
  const [inviting, setInviting] = useState(false);
  if (session.org_id === null) {
    return (
      <>
        <PageIntro destination="customers" />
        <section className="panel">
          <Empty title="Customers belong to a partner">
            <p>
              You are reading a workspace. Switch to your partner to see the customers it serves
              and to invite one.
            </p>
          </Empty>
        </section>
      </>
    );
  }
  return (
    <>
      <PageIntro
        destination="customers"
        action={
          readOnly ? undefined : (
            <button
              type="button"
              className="btn btn--primary"
              aria-expanded={inviting}
              aria-controls={inviting ? "invite-customer" : undefined}
              onClick={() => setInviting((was) => !was)}
            >
              Invite a customer
            </button>
          )
        }
      />
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {readOnly || !inviting ? null : <InviteCustomer onInvite={onInvite} />}
      <section className="panel">
        <div className="panel__head">
          <h2>Your customers</h2>
          {customers === null ? null : <Chip>{customers.length} shown</Chip>}
        </div>
        {customers === null && error === null ? (
          <p className="muted panel__body" role="status">
            Reading your customers…
          </p>
        ) : null}
        {customers !== null && customers.length === 0 ? (
          <Empty title="No customers yet" mark="○">
            <p>Invite your first customer. Its admin decides what you may do there.</p>
          </Empty>
        ) : null}
        {customers !== null && customers.length > 0 ? (
          <div className="table-wrap" tabIndex={0} role="region" aria-label="Customers">
            <table className="table customer-table">
              <thead>
                <tr>
                  <th scope="col">Customer workspace</th>
                  <th scope="col" className="num">Accounts</th>
                  <th scope="col">Setup</th>
                  <th scope="col" className="num">Open investigations</th>
                  <th scope="col">Access</th>
                  <th scope="col" className="num">Credits this period</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.workspace_id}>
                    <td>
                      {customer.customer_name && customer.customer_name !== customer.name ? (
                        <span className="muted">{customer.customer_name}</span>
                      ) : null}
                      <strong>{customer.name}</strong>
                      {customer.may_enter ? (
                        <button
                          type="button"
                          className="btn btn--small"
                          onClick={() => void onEnter({ workspace_id: customer.workspace_id })
                            .then(() => window.location.assign("/"))
                            .catch(() => { /* The context transition presents the server's refusal. */ })}
                        >
                          Open workspace<span className="sr-only">: {customer.name}</span>
                        </button>
                      ) : (
                        <small>{waitingOn(customer)}</small>
                      )}
                    </td>
                    <td className="num">{countOf(customer.accounts)}</td>
                    <td>
                      {customer.progress === null || customer.progress === undefined ? (
                        <small>Shown once you have access</small>
                      ) : (
                        <Progress name={customer.name} progress={customer.progress} />
                      )}
                    </td>
                    <td className="num">{countOf(customer.open_investigations)}</td>
                    <td>
                      <Access customer={customer} readOnly={readOnly} onRequestAgain={onRequestAgain} />
                    </td>
                    <td className="num">
                      <Credits bill={customer.bill ?? null} payer={customer.payer === true} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
}

/** Why a customer cannot be opened yet, in the customer's own terms. */
function waitingOn(customer: CustomerRow): string {
  if (customer.grants.investigate === "revoked") return "Access revoked";
  if (customer.grants.investigate === "declined") return "Access declined";
  return customer.payer ? "No access yet" : "Awaiting the customer's answer";
}

/** A count the organisation may read, or a dash where the customer has not let it in. */
function countOf(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toLocaleString();
}

const STEPS: Array<[keyof NonNullable<CustomerRow["progress"]>, string]> = [
  ["owner_accepted", "Owner joined"],
  ["account_connected", "AWS account connected"],
  ["alert_source", "Alert source live"],
  ["first_run", "First investigation"],
];

/** Setup as one bar, and the next step in words. */
function Progress({
  name,
  progress,
}: {
  name: string;
  progress: NonNullable<CustomerRow["progress"]>;
}) {
  const done = STEPS.filter(([step]) => progress[step]).length;
  const next = STEPS.find(([step]) => !progress[step]);
  return (
    <div className="customer-setup">
      <progress
        className="setup-bar"
        value={done}
        max={STEPS.length}
        aria-label={`${name} setup progress`}
        aria-valuetext={`${done} of ${STEPS.length} steps`}
      />
      <small>
        {done} of {STEPS.length}
        {next === undefined ? " · set up" : ` · next: ${next[1]}`}
      </small>
    </div>
  );
}

/** Full is every capability granted, Limited some, None none (#815, the owner's answer). */
function accessOf(grants: CustomerRow["grants"]): "Full" | "Limited" | "None" {
  const granted = CAPABILITIES.filter((c) => grants[c] === "granted").length;
  if (granted === 0) return "None";
  return granted === CAPABILITIES.length ? "Full" : "Limited";
}

const ACCESS_TONE = { Full: "good", Limited: "warn", None: undefined } as const;

/** The access level as one chip; each capability's answer and the ask-again behind it. */
function Access({
  customer,
  readOnly,
  onRequestAgain,
}: {
  customer: CustomerRow;
  readOnly: boolean;
  onRequestAgain: (workspaceId: string, capabilities: Capability[]) => Promise<void>;
}) {
  const level = accessOf(customer.grants);
  const asked = CAPABILITIES.filter((c) => customer.grants[c] !== undefined);
  return (
    <div className="stack stack--tight">
      <Chip tone={ACCESS_TONE[level]}>{level}</Chip>
      {asked.length > 0 || readOnly || customer.may_ask !== true ? null : (
        // A customer the organisation only pays for (#678): nothing answered yet, and an ask.
        <details className="customer-grants">
          <summary>Ask for access</summary>
          <AskAgain customer={customer} onRequestAgain={onRequestAgain} />
        </details>
      )}
      {asked.length === 0 ? null : (
        <details className="customer-grants">
          <summary>What they answered</summary>
          <ul>
            {asked.map((capability) => (
              <li key={capability}>
                {CAPABILITY_TEXT[capability].title}: {STATE_TEXT[customer.grants[capability]!]}
              </li>
            ))}
          </ul>
          {customer.consent_records === null || customer.consent_records === undefined ? null : (
            <small>
              {customer.consent_records} standing consent record
              {customer.consent_records === 1 ? "" : "s"} · answered by the customer, never by you
            </small>
          )}
          {readOnly ? null : <AskAgain customer={customer} onRequestAgain={onRequestAgain} />}
        </details>
      )}
    </div>
  );
}

/** The customer's credits on the payer's bill this period (#632, D6). A dash when none. */
function Credits({ bill, payer }: { bill: NonNullable<CustomerRow["bill"]> | null; payer: boolean }) {
  if (bill === null) return <>—</>;
  return (
    <>
      <strong className="tab">{bill.credits_used.toLocaleString()}</strong>
      <small>
        of {bill.included_credits.toLocaleString()} included
        {bill.overage_credits > 0 ? ` · ${bill.overage_credits.toLocaleString()} over` : ""} ·{" "}
        {bill.total} {bill.currency}
      </small>
      {payer ? <small>You pay for this workspace</small> : null}
    </>
  );
}

/** What each state reads as. */
const STATE_TEXT: Record<GrantState, string> = {
  granted: "allowed",
  pending: "pending",
  declined: "declined",
  revoked: "revoked",
};

/** The capabilities with no live row: declined, revoked, or never asked. */
function askable(customer: CustomerRow): Capability[] {
  return CAPABILITIES.filter((c) => {
    const state = customer.grants[c];
    return state === undefined || state === "declined" || state === "revoked";
  });
}

function AskAgain({
  customer,
  onRequestAgain,
}: {
  customer: CustomerRow;
  onRequestAgain: (workspaceId: string, capabilities: Capability[]) => Promise<void>;
}) {
  const [chosen, setChosen] = useState<Capability[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = askable(customer);
  if (options.length === 0) return null;
  const ask = async () => {
    setBusy(true);
    setError(null);
    try {
      await onRequestAgain(customer.workspace_id, chosen);
      setChosen([]);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack stack--tight" data-testid="ask-again">
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <div className="row row--wrap">
        {options.map((capability) => (
          <label key={capability} className="checkline">
            <input
              type="checkbox"
              checked={chosen.includes(capability)}
              onChange={() =>
                setChosen((was) =>
                  was.includes(capability) ? was.filter((c) => c !== capability) : [...was, capability],
                )
              }
            />
            <span>{CAPABILITY_TEXT[capability].title}</span>
          </label>
        ))}
        <button
          type="button"
          className="btn btn--small"
          disabled={busy || chosen.length === 0}
          onClick={() => void ask()}
        >
          {busy ? "Asking…" : "Ask for these"}
        </button>
      </div>
    </div>
  );
}

function InviteCustomer({
  onInvite,
}: {
  onInvite: (body: {
    name: string;
    admin_email: string;
    capabilities: Capability[];
    creation_key: string;
  }) => Promise<Created>;
}) {
  const [key, setKey] = useState(creationKey);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [asked, setAsked] = useState<Capability[]>(["investigate"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [made, setMade] = useState<Created | null>(null);
  const emailInput = useRef<HTMLInputElement>(null);

  const toggle = (capability: Capability) =>
    setAsked((was) =>
      was.includes(capability) ? was.filter((c) => c !== capability) : [...was, capability],
    );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setEmailError(null);
    setBusy(true);
    try {
      const created = await onInvite({
        name,
        admin_email: email,
        capabilities: CAPABILITIES.filter((c) => asked.includes(c)),
        creation_key: key,
      });
      setMade(created);
      setKey(creationKey());
      setName("");
      setEmail("");
    } catch (failure) {
      const refused = addressRefusal(failure, "admin_email");
      if (refused === null) setError(messageOf(failure));
      else {
        setEmailError(refused);
        emailInput.current?.focus();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel customer-invite" id="invite-customer">
      <div className="panel__head">
        <h2>Invite a customer</h2>
      </div>
      <form className="panel__body" onSubmit={submit} aria-busy={busy} data-testid="invite-customer">
        <p className="muted">
          This creates the customer and its own workspace and invites its admin. The capabilities
          you ask for wait for their answer.
        </p>
        {made === null ? null : (
          <Notice tone="good">
            {made.replayed
              ? `${made.name} was already invited from an earlier click; nothing new was made.`
              : `${made.name} is created. An invitation is on its way to ${made.invite.email}, and your request for ${made.requested.length === 0 ? "no capabilities" : made.requested.map((c) => CAPABILITY_TEXT[c].title.toLowerCase()).join(", ")} waits for their answer.`}
          </Notice>
        )}
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        <Field
          id="customer-name"
          label="Customer name"
          name="name"
          required
          autoFocus
          hint="The customer and its first workspace take this name."
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Field
          id="customer-admin"
          ref={emailInput}
          label="Their admin's email"
          name="admin_email"
          type="email"
          required
          hint="They receive the invitation, own the workspace, and answer your request."
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={emailError}
        />
        <fieldset className="stack stack--tight">
          <legend className="field__label">What you are asking for</legend>
          {CAPABILITIES.map((capability) => (
            <label key={capability} className="checkline">
              <input
                type="checkbox"
                name="capabilities"
                value={capability}
                checked={asked.includes(capability)}
                onChange={() => toggle(capability)}
              />
              <span>
                <strong>{CAPABILITY_TEXT[capability].title}</strong>
                <br />
                <small className="muted">{CAPABILITY_TEXT[capability].detail}</small>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Inviting…" : "Create the customer and invite its admin"}
          </button>
        </div>
      </form>
    </section>
  );
}
